import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import ListingCard from "@/components/ListingCard";

/**
 * Long-tail SEO page: /{category}/{city}
 * Example: /fotografi/roma, /property-manager/milano
 * Hundreds of auto-generated high-intent pages.
 */
export default function CategoryCityLanding() {
  const { category: catSlug, city: citySlug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/seo/category-city/${catSlug}/${citySlug}`)
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, [catSlug, citySlug]);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h1 className="font-heading font-black text-3xl">Pagina non trovata</h1>
        <button onClick={() => nav("/")} className="mt-6 px-4 py-2 rounded bg-[#FFBE00] text-black font-bold uppercase text-xs">← Home</button>
      </div>
    );
  }
  if (!data) return <div className="text-center py-20 text-zinc-400">Caricamento…</div>;

  const { city, category, listings, total } = data;
  const catName = category.name;
  const title = `${catName} a ${city.name} per affitti brevi | HostMarket`;
  const description = `${total} ${catName.toLowerCase()} a ${city.name} specializzati in affitti brevi. Fai crescere il tuo business con professionisti verificati.`;
  const url = `${window.location.origin}/${catSlug}/${citySlug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: title,
    description,
    url,
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin },
        { "@type": "ListItem", position: 2, name: catName, item: `${window.location.origin}/categoria/${catSlug}` },
        { "@type": "ListItem", position: 3, name: city.name, item: url },
      ],
    },
  };

  return (
    <div data-testid="cat-city-landing" className="bg-black">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={`${catName.toLowerCase()} ${city.name}, ${catName.toLowerCase()} affitti brevi ${city.name}, ${catName.toLowerCase()} airbnb ${city.name}, professionisti ${city.name}`} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-14">
        <nav className="text-xs text-zinc-500 mb-4 flex items-center gap-2">
          <Link to="/" className="hover:text-[#FFBE00]">Home</Link> ›
          <Link to={`/categoria/${catSlug}`} className="hover:text-[#FFBE00]">{catName}</Link> ›
          <span className="text-zinc-300">{city.name}</span>
        </nav>

        <div className="text-xs uppercase tracking-[0.25em] text-[#FFBE00] mb-3">· {city.name} · {category.name}</div>
        <h1 className="font-heading font-black text-3xl sm:text-5xl leading-[1]">
          <span className="text-[#FFBE00]">{catName}</span> a {city.name}<br />
          per affitti brevi.
        </h1>
        <p className="mt-4 max-w-2xl text-zinc-300">
          <strong className="text-white">{total}</strong> {catName.toLowerCase()} a {city.name} ({city.region}) specializzati nel settore degli affitti brevi, short term rentals e gestione appartamenti turistici.
        </p>

        {listings.length === 0 ? (
          <div className="hm-card p-10 text-center mt-8">
            <h2 className="font-heading font-bold text-xl">Sei un {catName.toLowerCase().replace(/i$/, "o")} a {city.name}?</h2>
            <p className="text-sm text-zinc-400 mt-2">Nessun annuncio in questa combinazione. Pubblica il tuo e intercetta da subito host e property manager locali.</p>
            <Link to="/registrati" className="mt-5 inline-block px-6 py-3 rounded-md bg-[#FFBE00] text-black font-bold uppercase text-xs">Pubblica annuncio →</Link>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {listings.map((l) => <ListingCard key={l.id} listing={l} />)}
          </div>
        )}

        <div className="mt-14 pt-8 border-t border-zinc-900">
          <h2 className="font-heading font-bold text-lg mb-3">Esplora di più</h2>
          <div className="flex flex-wrap gap-2">
            <Link to={`/categoria/${catSlug}`} className="px-3 py-1.5 text-xs rounded-full border border-zinc-700 hover:border-[#FFBE00] hover:text-[#FFBE00]">Tutti i {catName.toLowerCase()}</Link>
            <Link to={`/professionisti-affitti-brevi/${citySlug}`} className="px-3 py-1.5 text-xs rounded-full border border-zinc-700 hover:border-[#FFBE00] hover:text-[#FFBE00]">Tutti i professionisti a {city.name}</Link>
            <Link to="/categorie" className="px-3 py-1.5 text-xs rounded-full border border-zinc-700 hover:border-[#FFBE00] hover:text-[#FFBE00]">Tutte le categorie</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
